// Réglages du module Recrutement — sous la carte « Recrutement &
// onboarding » de Modules. Une seule clé serveur (PUT
// /api/recrutement/settings, validée en bloc : jours croissants,
// décision avant la fin d'essai), donc un brouillon local et un bouton
// Enregistrer plutôt qu'un enregistrement au blur champ par champ.
//
// Aperçu : la barre est recalculée sur les scores jury déjà présents
// dans le pipeline (gates.jury) — rien de plus à charger.

import { useEffect, useMemo, useState } from 'react';
import { REC_SETTINGS_DEFAULT, recBar, recCpPlan, type RecSettings } from '@pointage/shared';
import { toast } from '../../store/toast.js';
import { useMe, useRecPipeline, useRecSettings, useRecSettingsSave } from '../../api/hooks.js';

const CP3 = ['Alerte précoce', 'Revue 1', 'Pouls équipe', 'Revue 2', 'Décision finale'];
const CP6 = ['Alerte précoce', 'Revue 1', 'Revue 2', 'Pouls équipe', 'Revue 3'];

/** Même contrôle que le serveur — affiché avant l'envoi. */
function problems(d: RecSettings): string[] {
  const out: string[] = [];
  const sc = (v: number) => v >= 1 && v <= 5;
  if (![d.barOverall, d.barPerf, d.barMin, d.testPass].every(sc)) out.push('Les seuils sont des notes entre 1 et 5.');
  const inc = (a: number[]) => a.every((x, i) => Number.isInteger(x) && x >= 1 && (i === 0 || x > a[i - 1]!));
  if (!inc(d.cp3) || d.cp3[4]! >= 90) out.push('Essai 3 mois : jours croissants, décision avant J+90.');
  if (!inc(d.cp6) || d.cp6[4]! >= 180) out.push('Essai 6 mois : jours croissants, avant J+180.');
  if (!Number.isInteger(d.cp6FinalBefore) || d.cp6FinalBefore < 1 || d.cp6FinalBefore > 60) out.push('Décision 6 mois : entre 1 et 60 jours avant la fin.');
  if (!Number.isInteger(d.testFee) || d.testFee < 0) out.push('Indemnité : montant entier positif.');
  const final6 = recCpPlan(6, d).at(-1)!.day;
  if (d.cp6[4]! >= final6) out.push(`Essai 6 mois : la dernière revue doit précéder la décision (J+${final6}).`);
  return out;
}

const fmt = (v: number) => v.toFixed(1).replace('.', ',');

function Num({ value, onChange, step = 0.1, min, max, prefix, suffix, label, testid }: {
  value: number; onChange: (v: number) => void; step?: number; min?: number; max?: number;
  prefix?: string; suffix?: string; label: string; testid: string;
}) {
  return (
    <label className="rset-num">
      <span className="rset-num-lbl">{label}</span>
      <span className="rset-num-in">
        {prefix && <span className="rset-suffix">{prefix}</span>}
        <input className="finput" type="number" inputMode="decimal" step={step} min={min} max={max}
          value={Number.isFinite(value) ? value : ''} aria-label={label} data-testid={testid}
          onChange={(e) => onChange(e.target.value === '' ? NaN : Number(e.target.value))} />
        {suffix && <span className="rset-suffix">{suffix}</span>}
      </span>
    </label>
  );
}

export function RecSettingsPanel({ ping }: { ping: () => void }) {
  const me = useMe();
  const canView = (me.data?.permissions ?? []).includes('recrutement.view');
  const settings = useRecSettings(canView);
  const save = useRecSettingsSave();
  const pipeline = useRecPipeline(canView);
  const current = settings.data ?? REC_SETTINGS_DEFAULT;
  const [d, setD] = useState<RecSettings>(current);
  useEffect(() => { if (settings.data) setD(settings.data); }, [JSON.stringify(settings.data ?? null)]);

  const set = <K extends keyof RecSettings>(k: K, v: RecSettings[K]) => setD((x) => ({ ...x, [k]: v }));
  const setDay = (k: 'cp3' | 'cp6', i: number, v: number) => setD((x) => ({ ...x, [k]: x[k].map((n, j) => (j === i ? v : n)) }));

  const errs = problems(d);
  const dirty = JSON.stringify(d) !== JSON.stringify(current);

  // Candidats encore en lice avec une note jury complète.
  const preview = useMemo(() => {
    const rows = (pipeline.data ?? [])
      .filter((a) => !a.closedAt && a.gates.jury?.complete)
      .map((a) => ({
        id: a.id,
        name: `${a.candidate.firstName} ${a.candidate.lastName}`,
        overall: a.gates.jury!.overall,
        before: recBar(a.gates.jury!, current).state,
        after: errs.length ? recBar(a.gates.jury!, current) : recBar(a.gates.jury!, d),
      }));
    return {
      rows,
      ok: rows.filter((r) => r.after.state === 'ok').length,
      flips: rows.filter((r) => r.before !== r.after.state),
    };
  }, [pipeline.data, JSON.stringify(d), JSON.stringify(current), errs.length]);

  const submit = () => {
    if (errs.length) return;
    save.mutate(d, {
      onSuccess: ping,
      onError: () => toast.danger('Échec de l’enregistrement des réglages recrutement.'),
    });
  };

  if (!canView) {
    return <div className="rset" data-testid="rec-settings"><p className="mod-opt-desc">Réglages visibles avec le droit « Recrutement — consulter ».</p></div>;
  }
  if (!settings.data) return <div className="rset"><p className="mod-opt-desc">Chargement…</p></div>;

  const plan6 = recCpPlan(6, d);

  return (
    <div className="rset" data-testid="rec-settings">
      <div className="rset-grp">
        <div className="rset-h">Barre d’embauche</div>
        <div className="rset-sub">On compare un candidat à la barre, pas aux autres candidats. Sous la barre, l’offre est bloquée (dérogation Boss motivée).</div>
        <div className="rset-row">
          <Num label="Score global ≥" value={d.barOverall} min={1} max={5} onChange={(v) => set('barOverall', v)} testid="rset-barOverall" />
          <Num label="Performance ≥" value={d.barPerf} min={1} max={5} onChange={(v) => set('barPerf', v)} testid="rset-barPerf" />
          <Num label="Aucune dimension <" value={d.barMin} min={1} max={5} onChange={(v) => set('barMin', v)} testid="rset-barMin" />
        </div>
        {preview.rows.length > 0 && (
          <div className="rset-preview" data-testid="rset-preview">
            <span className="rset-pv-count"><b>{preview.ok}</b> / {preview.rows.length} candidat{preview.rows.length > 1 ? 's' : ''} noté{preview.rows.length > 1 ? 's' : ''} au-dessus de la barre</span>
            {preview.flips.length > 0 && (
              <ul className="rset-flips">
                {preview.flips.map((r) => (
                  <li key={r.id} className={r.after.state === 'ok' ? 'up' : 'down'}>
                    <b>{r.name}</b> ({r.overall == null ? '—' : fmt(r.overall)}) passe {r.after.state === 'ok' ? 'au-dessus' : 'sous la barre'}
                    {r.after.state === 'ko' && r.after.fails[0] ? ` — ${r.after.fails[0]}` : ''}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>

      <div className="rset-grp">
        <div className="rset-h">Cas pratique</div>
        <div className="rset-sub">Sous la note de passage, le candidat ne peut pas avancer en entretien.</div>
        <div className="rset-row">
          <Num label="Note de passage ≥" value={d.testPass} min={1} max={5} onChange={(v) => set('testPass', v)} testid="rset-testPass" />
          <Num label="Indemnité par défaut" value={d.testFee} step={500} min={0} suffix="DZD" onChange={(v) => set('testFee', v)} testid="rset-testFee" />
        </div>
      </div>

      <div className="rset-grp">
        <div className="rset-h">Période d’essai — points de contrôle</div>
        <div className="rset-sub">Jours comptés depuis la date d’entrée. Les points déjà planifiés gardent leur date ; les nouveaux onboardings prennent ces valeurs.</div>
        <div className="rset-cap">Essai 3 mois</div>
        <div className="rset-row days">
          {CP3.map((l, i) => (
            <Num key={l} label={l} step={1} min={1} max={89} prefix="J+" value={d.cp3[i]!} onChange={(v) => setDay('cp3', i, v)} testid={`rset-cp3-${i}`} />
          ))}
        </div>
        <div className="rset-cap">Essai 6 mois</div>
        <div className="rset-row days">
          {CP6.map((l, i) => (
            <Num key={l} label={l} step={1} min={1} max={179} prefix="J+" value={d.cp6[i]!} onChange={(v) => setDay('cp6', i, v)} testid={`rset-cp6-${i}`} />
          ))}
          <Num label="Décision : fin −" step={1} min={1} max={60} suffix="j" value={d.cp6FinalBefore} onChange={(v) => set('cp6FinalBefore', v)} testid="rset-cp6FinalBefore" />
        </div>
        <div className="rset-sub">Décision finale de l’essai 6 mois : J+{Number.isFinite(plan6.at(-1)!.day) ? plan6.at(-1)!.day : '—'}.</div>
      </div>

      {errs.length > 0 && (
        <ul className="rset-errs" role="alert" data-testid="rset-errs">{errs.map((e) => <li key={e}>{e}</li>)}</ul>
      )}
      <div className="rset-actions">
        <button className="btn btn-sm btn-ghost" disabled={!dirty} onClick={() => setD(current)}>Annuler</button>
        <button className="btn btn-sm btn-ghost" onClick={() => setD(REC_SETTINGS_DEFAULT)} data-testid="rset-defaults">Valeurs par défaut</button>
        <button className="btn btn-sm btn-primary" disabled={!dirty || errs.length > 0 || save.isPending}
          onClick={submit} data-testid="rset-save">{save.isPending ? 'Enregistrement…' : 'Enregistrer'}</button>
      </div>
    </div>
  );
}
